import Vue from 'vue'
import Vuex, { StoreOptions, Store } from 'vuex';
import { nanoid } from "nanoid";

import {
  ProductMainInfo,
  CustomerInfo,
  SupplierOrganization,
  Project,
  ProjectItem,
  SaveOrUpdateModel,
  ProductCharacteristic,
  Organization,
  SaveResult,
  ProductCharacteristicTypes,
  FormModeType,
  PurchaserTermsType,
  ApplicationRequirements,
  PurchaseType,
  StoreParams,
  OrganizationInfo,
  Subcontracts,
  SubcontractRequest,
  SubcontractsTree,
  SubcontractPosition,
  SubcontractRequestResult,
  SubcontractProject,
  SubcontractProjectForEdit,
}
  from '@/types/supplier-search'

import { UnitsModel, FileModel, OkatoModel } from '@/types/common'
import { DefaultListModel } from "@/components/controls/select/select-searchable/types";

import { currentProject, currentProjectOffersRequest, currentPosition, emptyCharacteristic, initialMainProductinfo, currentSubcontracts } from './_initialStateObjects';
import { suppliers } from "./modules/suppliers";

import { objectDeepClone } from '@/utils/object'
import { AreArraysEqual, compareOrganizations } from '@/utils/helpers'

import cloneDeep from 'lodash.clonedeep'

import * as mapper from './mapper'

import { TenderWebApi } from '@/api/tenderApi'
import { BaseTenderWebApi } from '@/api/baseTenderApi'
import { numeric } from 'vuelidate/lib/validators';

Vue.use(Vuex);

const getState = (params: StoreParams): RootStateSupplierSearch => ({
  isAuthentificated: false,
  mainProductinfo: { ...initialMainProductinfo } as ProductMainInfo,
  supplierOrganization: {} as SupplierOrganization,
  customerOrganization: {} as Organization,
  projects: [] as Project[],
  currentProject: cloneDeep(params.type === PurchaseType.CommercialOffersRequest ? currentProjectOffersRequest : currentProject),
  currentSubcontracts: cloneDeep(currentSubcontracts),
  subcontractsTree: {} as SubcontractsTree,
  subcontractOriginDeliveryTerms: {} as SubcontractProject,
  subcontractTenderId: -1,
  subcontractKladr: '',
  originChangedProject: cloneDeep(params.type === PurchaseType.CommercialOffersRequest ? currentProjectOffersRequest : currentProject),
  isNewProject: true,
  isNewPosition: true,
  isMultiPosition: false,
  singleDeliveryAddress: true,
  currentPosition: cloneDeep(currentPosition),
  originChangedPosition: cloneDeep(currentPosition),
  errorMessage: undefined,
  modeType: FormModeType.ByProduct,
  okatoList: [] as OkatoModel[],
  supplierFilter: {},
  initSupplierSearch: true as boolean,
  supplierItems: [],
  supplierItemsPaging: {},
  supplierItemsTotalCount: 0
});

const getStoreOptions = (params: StoreParams): StoreOptions<RootStateSupplierSearch> => ({
  modules: {
    suppliers: suppliers,
  },
  state: cloneDeep(getState(params)),
  actions: {
    async getProjectsInfo({ commit, state }) {
      try {
        let projects: Project[] = [];
        if (state.isAuthentificated) {
          const response = await TenderWebApi.getProjects();

          if (!response || !response.data || !response.data.Projects) {
            console.error("getProjects error");
            commit('setProjectsInfo', []);
            return;
          }

          projects = response.data.Projects;
        }

        mapper.PrepeareProjectsInfo(projects);
        commit('setProjectsInfo', projects)
      }
      catch (error) {
        console.error(error);
        commit('setProjectsInfo', [])
      }
    },
    async saveProject({ commit, getters }) {
      try {
        commit("preparePositions");
        commit("mapProductInfoToPosition");
        const response = await TenderWebApi.saveProject(getters.dataForUpdateOrCreate);
        //воткнуть обработчик запросов
        this.commit('setSaveResult', response.data);

        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    },
    async updateProject({ commit, getters }) {
      try {
        commit("preparePositions");
        commit("mapProductInfoToPosition");
        const response = await TenderWebApi.updateProject(getters.dataForUpdateOrCreate);
        this.commit('setSaveResult', response.data);

        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    },
    async createCommercialOffersRequest({ commit, getters }) {
      try {
        commit("preparePositions");
        const response = await TenderWebApi.createCommercialOffersRequest(getters.dataForUpdateOrCreate);
        this.commit('setSaveResult', response.data);

        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    },
    async updateCommercialOffersRequest({ commit, getters }) {
      try {
        commit("preparePositions");
        const response = await TenderWebApi.updateCommercialOffersRequest(getters.dataForUpdateOrCreate);
        this.commit('setSaveResult', response.data);

        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    },
    async createSubcontracts({ getters }) {
      try {
        const response = await BaseTenderWebApi.createSubcontracts(getters.subcontractsSaveData);
        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    },
    async editSubcontracts({ getters }) {
      try {
        const response = await BaseTenderWebApi.editSubcontracts(getters.subcontractsSaveData);
        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    },
    async saveTrade({ commit }, trade: SubcontractProjectForEdit) {
      try {
        const response = await BaseTenderWebApi.saveTrade(trade);
        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    },
    async getUserInfo({ commit }) {
      try {
        const response = await TenderWebApi.getUserInfo();
        commit('setUserInfo', response.data);
      }
      catch (error) {
        console.log(error);
      }
    },
    async getSubcontractsTree({ commit }, params: SubcontractRequest) {
      try {
        const response = await BaseTenderWebApi.getOffersStatistic(params);
        this.commit('setSaveSubcontracts', response.data);
        this.commit('setSaveSubcontractsRequestData', params);

        return response
      } catch (error) {
        this.commit('setErrorMessage', error.message);
        console.error(error);
      }
    }, 
    async getTimeZones({ commit }) {
      try {
        const response = await BaseTenderWebApi.getTimeZones();
        return response
      } catch (error) {
        console.error(error);
      }
    },
    setSubcontractPositions({ commit }, data: SubcontractPosition[]) {
      commit('setSubcontractPositions', data);
    },
    clearStore({ commit }) {
      commit('clearStore', cloneDeep(getState(params)));
    },
    setStoreToInitialState({ commit }) {
      commit('setStoreToInitialState');
    },
    setMode({ commit }, payload: FormModeType) {
      commit('setMode', payload);
    },
    setCurrentPositionPrice({ commit }, payload: number) {
      commit('setCurrentPositionPrice', payload);
    },
    setCurrentPositionBudget({ commit }, payload: number) {
      commit('setCurrentPositionBudget', payload);
    },
    clearCurrentPositionPriceAndBudget({ commit }) {
      commit('clearCurrentPositionPriceAndBudget');
    },
    setMainProductinfo({ commit, state }, data: ProductMainInfo) {
      mapper.SetMainProductinfo(state, data);
      commit('setMainProductinfo', data);
    },
    setDefaultProject({ commit }) {
      commit('setCurrentProject', { ...currentProject });
    },
    updateCurrentProject({ commit }, data: Project) {
      commit('setCurrentProject', data);
    },
    setDefaultPosition({ commit }) {
      commit('setCurrentPosition', { ...currentPosition });
    },
    updateCurrentPosition({ commit }, data: ProjectItem) {
      commit('setCurrentPosition', data);
    },
    addCharacteristic({ commit, state }) {
      const characteristic: ProductCharacteristic = objectDeepClone(emptyCharacteristic);
      const characteristics = [...state.currentPosition.Characteristics];
      characteristic.LotItem = (state.currentPosition.Id && state.currentPosition.Id > 0) ? state.currentPosition.Id : 0;

      if (characteristics.length > 0) {
        const prevItem = characteristics[characteristics.length - 1];
        characteristic.Value.Type = ProductCharacteristicTypes.String;

        if (prevItem.Index === 0) characteristic.Index = 1;

        if (prevItem.Index) characteristic.Index = prevItem.Index + 1;

        if (characteristic.Index === 0 || characteristic.Index) {
          characteristics.push(characteristic);
        }
      }
      else {
        characteristic.Index = 0;
        characteristic.Value.Type = ProductCharacteristicTypes.String;
        characteristics.push(characteristic);
      }
      commit('setCharacteristicsToPosition', characteristics)
    },
    deleteAllCharacteristic({ commit }) {
      commit('setCharacteristicsToPosition', [])
    },
    deleteCharacteristic({ commit, state }, id: number) {
      commit('setCharacteristicsToPosition', state.currentPosition.Characteristics.filter((item) => item.Index != id));
    },
    copyCharacteristicsToPosition({ commit, state }) {
      //проствляем index
      if (state.mainProductinfo && state.mainProductinfo.ProductCharacteristic) {
        const characteristicsList = state.mainProductinfo.ProductCharacteristic.map((item, index) => {
          item.Index = index;
          item.UnitSelectorOpen = false;
          item.ShowTypeSelector = false;
          item.TypeSelectorFocused = false;
          item.NameFocused = false;
          return { ...item };
        });
  
        commit('setCharacteristicsToPosition', characteristicsList);
      }
    },
    deleteFile({ commit, state }, index: number) {
      const files = state.currentProject.Additional.Files?.filter((file, fileIndex) => index != fileIndex) || [];
      commit('setFiles', files);
    },
    updateFiles({ commit }, files: File[]) {
      commit('setFiles', files);
    },
    async getRegionsKladrList({ commit }) {
      try {
        let okatos: OkatoModel[] = [];
        const response = await TenderWebApi.getRegionsKladrList();
        okatos = response.data;

        commit('setOkatoList', okatos)
      }
      catch (error) {
        console.error(error);
        commit('setOkatoList', [])
      }
    },
    async loadProject({ commit }, id: number) {
      try {
        const response = await TenderWebApi.getProject(id);
        const project = response.data.Project;

        project.ApplicationEndDateInner = new Date(project.ApplicationEndDateInner);
        project.Additional.ApplicationRequirements = project.Additional.ApplicationRequirements || ApplicationRequirements.CommercialOfferPhotoCopy;
        commit('setCurrentProject', project);
        commit('setIsNewProject', false);

        const isMultiPosition = project.Items.length > 1;
        commit('setIsMultiPosition', isMultiPosition);

        if (isMultiPosition) {
          commit('setPositions', project.Items);

          commit('setSingleDeliveryAddress', project.DeliveryAddress != null && project.DeliveryAddress.length > 0);
          if (project.DeliveryAddress) {
            commit('setCurrentPositionDeliveryAddress');
          }
        }
        else {
          commit('setCurrentPosition', project.Items[0]);
          commit('setPositions', []);
        }
      }
      catch (error) {
        console.error(error);
      }
    },
  },
  mutations: {
    clearStore(state: RootStateSupplierSearch) {
      state.isNewPosition = true;
      state.isNewProject = true;
      state.isMultiPosition = false;
      state.currentProject.PurchaserTermsType = params.type === PurchaseType.CommercialOffersRequest ? PurchaserTermsType.CustomerTerms : PurchaserTermsType.PurchaserTerms;
      state.currentPosition.Price = 0;
      state.currentPosition.Amount = 0;
      state.supplierFilter = {};
      state.initSupplierSearch = true;
      state.supplierItems = [];
      state.supplierItemsPaging = {};
      state.supplierItemsTotalCount = 0;
    },
    setStoreToInitialState(state: RootStateSupplierSearch) {
      state.currentProject = cloneDeep(params.type === PurchaseType.CommercialOffersRequest ? currentProjectOffersRequest : currentProject);
      state.currentPosition = cloneDeep(currentPosition);
      state.originChangedPosition = cloneDeep(currentPosition);
      state.isNewProject = true;
      state.isMultiPosition = false;
      state.singleDeliveryAddress = true;

      state.currentPosition.DeliveryAddress = state.customerOrganization.PostAddress === ", , " ? "" : state.customerOrganization.PostAddress;
      
      state.supplierFilter = {};
      state.initSupplierSearch = true;
      state.supplierItems = [];
      state.supplierItemsPaging = {};
      state.supplierItemsTotalCount = 0;
    },
    setMode(state: RootStateSupplierSearch, mode: FormModeType) {
      state.modeType = mode;
    },
    setFiles(state: RootStateSupplierSearch, files: FileModel[]) {
      state.currentProject.Additional.Files = [...files]
    },
    setCharacteristicsToPosition(state: RootStateSupplierSearch, data: ProductCharacteristic[]) {
      state.currentPosition.Characteristics = [...data]
    },
    setMainProductinfo(state: RootStateSupplierSearch, data: ProductMainInfo) {
      state.mainProductinfo = data
      state.mainProductinfo.TaxPercent = data.TaxPercent ? data.TaxPercent : 0
      state.supplierOrganization = data.SupplierOrganization
    },
    setProjectsInfo(state: RootStateSupplierSearch, projects: Project[]) {
      state.projects = [...projects];
    },
    setCurrentProject(state: RootStateSupplierSearch, currentProject: Project) {
      state.currentProject = { ...currentProject };
      state.originChangedProject = cloneDeep(currentProject);
    },
    setCurrentPositionPrice(state: RootStateSupplierSearch, price: number) {
      state.currentPosition.Price = price;
    },
    setCurrentPositionBudget(state: RootStateSupplierSearch, budget: number) {
      state.currentPosition.Budget = budget;
    },
    clearCurrentPositionPriceAndBudget(state: RootStateSupplierSearch) {
      state.currentPosition.Price = 0;
      state.currentPosition.Budget = 0;
    },
    setCurrentPosition(state: RootStateSupplierSearch, currentPosition: ProjectItem) {
      state.currentPosition = { ...currentPosition };
      state.originChangedPosition = cloneDeep(currentPosition);
    },
    setPositions(state: RootStateSupplierSearch, positions: ProjectItem[]) {
      positions.forEach(element => {
        if (!element.LocalId) element.LocalId = nanoid(6);
      });
      state.currentProject.Items = [...positions];
    },
    setQuantity(state: RootStateSupplierSearch, data: number) {
      state.mainProductinfo.Quantity = data;
    },
    setIsUnitPrice(state: RootStateSupplierSearch, data: boolean) {
      state.mainProductinfo.IsUnitPrice = data;
    },
    setPositionAmount(state: RootStateSupplierSearch, data: number) {
      state.currentPosition.Amount = data;
    },
    setPositionUnit(state: RootStateSupplierSearch, data: UnitsModel) {
      state.currentPosition.UnitCode = data.Code || data.UnitCode;
      state.currentPosition.UnitShortName = data.ShortName || data.UnitShortName;
      state.currentPosition.UnitName = data.Name || data.UnitName;
    },
    setOkpd2(state: RootStateSupplierSearch, data: DefaultListModel) {
      state.currentPosition.Okpd2 = data.Value ? data.Value as string : '';
      state.currentPosition.Okpd2Name = data.Name ? data.Name as string : '';
      state.mainProductinfo.Okpd2Code = state.currentPosition.Okpd2;
      state.mainProductinfo.Okpd2 = state.currentPosition.Okpd2Name;
    },
    setOkved2(state: RootStateSupplierSearch, data: DefaultListModel) {
      state.currentPosition.Okved2 = data.Value ? data.Value as string : '';
      state.currentPosition.Okved2Name = data.Name ? data.Name as string : '';
      state.mainProductinfo.Okved2Code = state.currentPosition.Okved2;
      state.mainProductinfo.Okved2 = state.currentPosition.Okved2Name;
    },
    setIsNewProject(state: RootStateSupplierSearch, data: boolean) {
      state.isNewProject = data;
    },
    setIsNewPosition(state: RootStateSupplierSearch, data: boolean) {
      state.isNewPosition = data;
    },
    setIsMultiPosition(state: RootStateSupplierSearch, data: boolean) {
      state.isMultiPosition = data;
    },
    setSingleDeliveryAddress(state: RootStateSupplierSearch, data: boolean) {
      state.singleDeliveryAddress = data;
    },
    setCurrentPositionDeliveryAddress(state: RootStateSupplierSearch) {
      state.currentPosition.DeliveryAddress = state.currentProject.DeliveryAddress || "";
      state.currentPosition.DeliveryAddressOkato = state.currentProject.DeliveryAddressOkato || "";
    },
    setUserInfo(state: RootStateSupplierSearch, data: CustomerInfo) {
      if (data && data.UserInfo && data.Organization) {
        state.isAuthentificated = data.UserInfo.IsAuthenticated;
        state.customerOrganization = data.Organization;
        const address = data.Organization.PostAddress === ", , " ? "" : data.Organization.PostAddress;
        state.currentPosition.DeliveryAddress = address;
        //state.currentPosition.DeliveryAddresses.push({ Name: address } as DeliveryAddress);
      }
    },
    setOrganizationInfo(state: RootStateSupplierSearch, data: Organization) {
      if (data) {
        state.customerOrganization = data;
      }
    },
    setSaveResult(state: RootStateSupplierSearch, data: SaveResult) {
      if (data.ProjectId && data.ProjectId > 0) {
        state.currentProject.Id = data.ProjectId;
      }
      if (data.TradeId && data.TradeId > 0) {
        state.tradeId = data.TradeId;
      }
      if (data.ErrorMessage) {
        state.errorMessage = data.ErrorMessage;
      }
      else {
        state.errorMessage = undefined;
      }
    },
    setSaveSubcontracts(state: RootStateSupplierSearch, data: SubcontractRequestResult) {
      if (data && data.Available)
        state.subcontractsTree.Available = data.Available;
      else state.subcontractsTree.Available = [];

      if (data && data.ExistedSubContracts)
        state.subcontractsTree.ExistedSubContracts = data.ExistedSubContracts;
      else state.subcontractsTree.ExistedSubContracts = [];

      if (data.ErrorMessage) {
        state.errorMessage = data.ErrorMessage;
      }
      else {
        state.errorMessage = undefined;
      }
    },
    setSaveSubcontractsRequestData(state: RootStateSupplierSearch, data: SubcontractRequest) {
      if (data && data.SupplierOrderId)
        state.subcontractTenderId = data.SupplierOrderId;

      if (data && data.KladrCode)
        state.subcontractKladr = data.KladrCode;
    },
    setSubcontractPositions(state: RootStateSupplierSearch, data: SubcontractPosition[]) {
      if (data && data.length > 0)
        state.currentSubcontracts.Positions = data;
    },
    setErrorMessage(state: RootStateSupplierSearch, data: string) {
      if (data) {
        state.errorMessage = data;
      }
    },
    setOkatoList(state: RootStateSupplierSearch, okatoList: OkatoModel[]) {
      state.okatoList = [...okatoList];
    },
    setOkato(state: RootStateSupplierSearch, data: OkatoModel) {
      state.currentPosition.DeliveryAddressOkato = data.Okato;
    },
    setSubcontractsOkato(state: RootStateSupplierSearch, data: OkatoModel) {
      state.currentSubcontracts.Project.DeliveryAddressOkato = data.Okato;
    },
    setApplicationRequirements(state: RootStateSupplierSearch, data: ApplicationRequirements) {
      state.currentProject.Additional.ApplicationRequirements = data;
    },
    addOrganizationToInvited(state: RootStateSupplierSearch, data: OrganizationInfo) {
      if (!state.currentProject.InvitedOrganizations) {
        state.currentProject.InvitedOrganizations = [];
      }
      if (!state.currentProject.InvitedOrganizations.some((item) => compareOrganizations(item.Inn, item.Kpp, data.Inn, data.Kpp))) {
        state.currentProject.InvitedOrganizations?.push(data);
      }
    },
    removeOrganizationFromInvited(state: RootStateSupplierSearch, data: OrganizationInfo) {
      if (state.currentProject.InvitedOrganizations) {
        state.currentProject.InvitedOrganizations =
          state.currentProject.InvitedOrganizations
            .filter((item) => !compareOrganizations(item.Inn, item.Kpp, data.Inn, data.Kpp));
      }
    },
    setInvitedOrganization(state: RootStateSupplierSearch, data: OrganizationInfo[]) {
      state.currentProject.InvitedOrganizations = data;
    },
    addPosition(state: RootStateSupplierSearch) {
      state.currentPosition.LocalId = nanoid(6);
      state.currentProject.Items.push(cloneDeep(state.currentPosition));
      state.currentPosition = cloneDeep(currentPosition);

      if (state.singleDeliveryAddress) {
        const lastPosition = state.currentProject.Items[state.currentProject.Items.length - 1];
        state.currentPosition.DeliveryAddress = lastPosition.DeliveryAddress;
        state.currentPosition.DeliveryAddressOkato = lastPosition.DeliveryAddressOkato;
      }
    },
    removePosition(state: RootStateSupplierSearch, data: string) {
      state.currentProject.Items = state.currentProject.Items.filter((item) => item.LocalId !== data);
    },
    preparePositions(state: RootStateSupplierSearch) {
      if (state.isMultiPosition) {
        if (state.singleDeliveryAddress) {
          const lastPosition = state.currentProject.Items[state.currentProject.Items.length - 1]
          state.currentProject.DeliveryAddress = lastPosition.DeliveryAddress;
          state.currentProject.DeliveryAddressOkato = lastPosition.DeliveryAddressOkato;
        }
      }
      else {
        state.currentProject.Items = [state.currentPosition];
      }
    },
    mapProductInfoToPosition(state: RootStateSupplierSearch) {
      mapper.ProductInfoToPosition(state.mainProductinfo, state.currentPosition);
      state.mainProductinfo.SearchQuery = "";
    },
    setSupplierFilter(state: RootStateSupplierSearch, data: {}) {
      state.supplierFilter = data;
      state.initSupplierSearch = false;
    },
    setSupplierItems(state: RootStateSupplierSearch, data: []) {
      state.supplierItems = data;
    },
    setSupplierItemsTotalCount(state: RootStateSupplierSearch, data: number) {
      state.supplierItemsTotalCount = data;
    },
    setSupplierItemsPaging(state: RootStateSupplierSearch, data: {}) {
      state.supplierItemsPaging = data;
    },
  },
  getters: {
    hasAnyProjects(state: RootStateSupplierSearch): boolean {
      return state.projects.length > 0
    },
    files(state: RootStateSupplierSearch) {
      return state.currentProject.Additional.Files;
    },
    projectsForSelect(state: RootStateSupplierSearch): Project[] {
      return state.projects
    },
    dataForUpdateOrCreate(state: RootStateSupplierSearch): SaveOrUpdateModel {
      return {
        ProductMainInfo: state.mainProductinfo,
        Project: state.currentProject
      }
    },
    subcontractsSaveData(state: RootStateSupplierSearch): Subcontracts {
      return {
        Positions: state.currentSubcontracts.Positions,
        Project: state.currentSubcontracts.Project,
      }
    },
    characteristicsForDisplay(state: RootStateSupplierSearch): ProductCharacteristic[] {
      return state.currentPosition.Characteristics;
    },
    isAuthentificated(state: RootStateSupplierSearch): boolean {
      return state.isAuthentificated;
    },
    errorMessage(state: RootStateSupplierSearch): string | undefined {
      return state.errorMessage;
    },
    notOriginModeType(state: RootStateSupplierSearch): boolean {
      return state.modeType != FormModeType.Origin;
    },
    okatoList(state: RootStateSupplierSearch): OkatoModel[] {
      return state.okatoList;
    },
    invitedOrganizations(state: RootStateSupplierSearch): OrganizationInfo[] | undefined {
      return state.currentProject.InvitedOrganizations;
    },
    okpd2(state: RootStateSupplierSearch): string | undefined {
      return state.currentPosition.Okpd2;
    },
    okpd2Name(state: RootStateSupplierSearch): string | undefined {
      return state.currentPosition.Okpd2Name;
    },
    name(state: RootStateSupplierSearch): string | undefined {
      return state.currentPosition.Name;
    },
    hasAddressesChanged(state: RootStateSupplierSearch): boolean {
      if (state.isMultiPosition && !state.singleDeliveryAddress) {
        return !AreArraysEqual(state.originChangedProject.Items.flatMap(i => i.DeliveryAddress), state.currentProject.Items.flatMap(i => i.DeliveryAddress));
      }
      else {
        return state.originChangedPosition.DeliveryAddress != state.currentPosition.DeliveryAddress;
      }
    },
    filter(state: RootStateSupplierSearch): {} | undefined{
      return state.supplierFilter;
    },
    initSupplierSearch(state: RootStateSupplierSearch): boolean | undefined{
      return state.initSupplierSearch;
    },
    supplierItems(state: RootStateSupplierSearch): [] | undefined{
      return state.supplierItems;
    },
    supplierItemsPaging(state: RootStateSupplierSearch): {} | undefined{
      return state.supplierItemsPaging;
    },
    supplierItemsTotalCount(state: RootStateSupplierSearch): number | undefined{
      return state.supplierItemsTotalCount;
    },
  }
});

export const getNewStore = (params: StoreParams): Store<RootStateSupplierSearch> => new Vuex.Store(cloneDeep(getStoreOptions(params)));